/**
 * Phone number utilities for formatting, normalizing, and building phone links.
 * Phone numbers are stored in E.164 format (e.g., +15551234567).
 */

export type PhoneProtocol = 'Tel' | 'Callto' | 'Sip' | 'Custom' | 'Disabled';

/**
 * Formats an E.164 phone number for display.
 * @param phone E.164 formatted phone number (e.g., "+15551234567")
 * @returns Formatted display string (e.g., "(555) 123-4567")
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  // Extract all digits
  const digits = phone.replace(/\D/g, '');

  if (!digits) return phone;

  // US format: 11 digits starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    // +15551234567 -> (555) 123-4567
    return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
  }

  // 10 digits (no country code)
  if (digits.length === 10) {
    // 5551234567 -> (555) 123-4567
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
  }

  // 7 digits (local number)
  if (digits.length === 7) {
    // 1234567 -> 123-4567
    return `${digits.substring(0, 3)}-${digits.substring(3, 7)}`;
  }

  // For other formats, return the original
  return phone;
}

/**
 * Builds an href for a phone link based on the protocol setting.
 * @param phone E.164 formatted phone number (e.g., "+15551234567")
 * @param protocol The phone protocol to use
 * @param customPrefix Custom prefix for Custom protocol
 * @returns The href string (e.g., "tel:+15551234567")
 */
export function buildPhoneHref(
  phone: string | null | undefined,
  protocol: PhoneProtocol,
  customPrefix?: string | null
): string | null {
  if (!phone || protocol === 'Disabled') return null;

  // Ensure phone is in E.164 format for the href
  const dialableNumber = getDialableNumber(phone);
  if (!dialableNumber) return null;

  switch (protocol) {
    case 'Tel':
      return `tel:${dialableNumber}`;
    case 'Callto':
      return `callto:${dialableNumber}`;
    case 'Sip':
      return `sip:${dialableNumber}`;
    case 'Custom':
      if (customPrefix) {
        return `${customPrefix}${dialableNumber}`;
      }
      return `tel:${dialableNumber}`; // Fallback to tel: if no custom prefix
    default:
      return null;
  }
}

/**
 * Gets the dialable number in E.164 format.
 * @param phone The phone number (E.164 or formatted)
 * @returns E.164 format phone number or null if invalid
 */
export function getDialableNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;

  // If already in E.164 format, return as-is
  if (phone.startsWith('+') && /^\+[1-9]\d{1,14}$/.test(phone)) {
    return phone;
  }

  // Otherwise normalize
  return normalizePhone(phone);
}

/**
 * Normalizes a phone number to E.164 format.
 * @param phone Raw phone input (e.g., "(555) 123-4567")
 * @returns E.164 formatted phone number (e.g., "+15551234567") or null if invalid
 */
export function normalizePhone(phone: string | null | undefined, defaultCountryCode: string = '1'): string | null {
  if (!phone) return null;

  // If already in E.164 format, validate and return
  if (phone.startsWith('+')) {
    const cleaned = '+' + phone.replace(/\D/g, '');
    if (/^\+[1-9]\d{1,14}$/.test(cleaned)) {
      return cleaned;
    }
  }

  // Extract all digits
  const digits = phone.replace(/\D/g, '');

  if (!digits) return null;

  // Handle different digit counts for US numbers
  if (digits.length === 10) {
    // Standard US 10-digit number: 5551234567 -> +15551234567
    return `+${defaultCountryCode}${digits}`;
  } else if (digits.length === 11 && digits.startsWith(defaultCountryCode)) {
    // Already has country code: 15551234567 -> +15551234567
    return `+${digits}`;
  } else if (digits.length === 7) {
    // Local 7-digit number - can't normalize properly
    return null;
  } else if (digits.length > 11) {
    // Could be international number
    return `+${digits}`;
  }

  return null;
}

/**
 * Validates whether a phone number appears to be valid.
 * @param phone The phone number to validate
 * @returns True if the phone number appears valid
 */
export function isValidPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;

  // Check if it's already in E.164 format
  if (/^\+[1-9]\d{1,14}$/.test(phone)) return true;

  // Try to normalize - if successful, it's valid
  return normalizePhone(phone) !== null;
}

/**
 * Formats a phone number as the user types, applying US formatting.
 * @param value The current input value
 * @returns Formatted phone string
 */
export function formatPhoneInput(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits for US numbers
  const truncated = digits.substring(0, 10);

  // Apply formatting based on length
  if (truncated.length === 0) {
    return '';
  } else if (truncated.length <= 3) {
    return `(${truncated}`;
  } else if (truncated.length <= 6) {
    return `(${truncated.substring(0, 3)}) ${truncated.substring(3)}`;
  } else {
    return `(${truncated.substring(0, 3)}) ${truncated.substring(3, 6)}-${truncated.substring(6)}`;
  }
}

/**
 * Strips formatting from a phone number, returning just digits.
 * @param formatted The formatted phone string
 * @returns Just the digits
 */
export function stripPhoneFormatting(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

/**
 * Formats a phone number with extension for display.
 * @param phone The phone number (E.164 or formatted)
 * @param extension Optional extension
 * @returns Formatted phone with extension (e.g., "(555) 123-4567 x123")
 */
export function formatPhoneWithExtension(phone: string | null | undefined, extension: string | null | undefined): string {
  const formatted = formatPhoneDisplay(phone);

  if (!formatted) return '';

  if (extension) {
    return `${formatted} x${extension}`;
  }

  return formatted;
}
